import { useId } from 'react';
import { styled } from '@/styles/stitches.config';
import { ComposableStyledComponentProps } from '@/types/composable-styled-component';

import { ScaleLinear, scaleLinear } from 'd3-scale';
import { motion } from 'framer-motion';
import { FC, useState, useEffect } from 'react';
import { Label as BaseLabel } from '../label';

export type BarChartItem = {
  label: string;
  value: number;
};

export type BarChartProps = ComposableStyledComponentProps<HTMLDivElement> & {
  data?: BarChartItem[];
  label: string;
  visible?: boolean;
  formatValue?: (d: number) => string;
};

const Root = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  variants: {
    isVisible: {
      true: {
        visibility: 'visible',
      },
      false: {
        visibility: 'hidden',
      },
    },
  },
});

const Container = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  gap: '3%',
});

const Label = styled(BaseLabel, {
  color: 'var(--color-text-light)',
  textAlign: 'center',
  fontSize: '12px',
  wordBreak: 'normal',
  padding: 0,
  paddingBlock: '$2',
  width: '30%',
  minWidth: '7.5rem',
  maxWidth: '15rem',
  '@bp2': {
    fontSize: 'clamp($1, 2vw, $2)',
  },
});

export const VerticalBarChart: FC<BarChartProps> = (props) => {
  const { css, data, label, visible = true, formatValue = (n) => String(n) } = props;

  if ((data?.length ?? 0) > 9) throw new Error('Maximum amount of bars to display is 9');

  const height = scaleLinear().range([0, 100]).domain([0, 1]);

  const [ids, setIds] = useState<{ [key: string]: string }>({});

  return (
    <Root isVisible={visible} css={css}>
      <BaseLabel
        css={{
          maxWidth: '70%',
          margin: 0,
          marginInline: 'auto',
          textAlign: 'center',
          color: 'var(--color-text-light)',
          fontWeight: '$medium',
        }}
      >
        {label}
      </BaseLabel>

      <Container css={{ flexGrow: 1 }}>
        {data &&
          data.map((d, i) => (
            <Bar
              key={d.label}
              formatValue={formatValue}
              visible={visible}
              colorIndex={(i % 3) as 0 | 1 | 2}
              height={height}
              handleId={(id) => {
                if (!ids[d.label]) {
                  setIds((currentIds) => ({ ...currentIds, [d.label]: id }));
                }
              }}
              {...d}
            />
          ))}
      </Container>

      <Container>
        {data &&
          data.map((d) => (
            <Label key={d.label} id={ids[d.label]}>
              {d.label}
            </Label>
          ))}
      </Container>
    </Root>
  );
};

type BarProps = {
  colorIndex: 0 | 1 | 2;
  visible: boolean;
  formatValue: (d: number) => string;
  value: number;
  label: string;
  height: ScaleLinear<number, number>;
  handleId: (id: string) => void;
};

export const StandaloneVerticalBarChart = (props: BarChartProps) => {
  return (
    <VerticalBarChart
      {...props}
      css={{
        alignSelf: 'stretch',
        paddingBlockStart: '$9',
        paddingBlockEnd: '$8',
        '@portrait': {
          paddingBlockStart: '$9',
          paddingBlockEnd: '$9',
        },
        '@bp4': {
          paddingBlockStart: '$7',
          paddingBlockEnd: '$7',
        },
      }}
    />
  );
};

const BarRoot = styled('div', {
  display: 'flex',
  flexDirection: 'column-reverse',
  height: '100%',
  width: '30%',
  minWidth: '7.5rem',
  maxWidth: '15rem',
});

const StyledBar = styled(motion.div, {
  borderRadius: '$2',
});

const Value = styled('span', {
  fontWeight: '$semibold',
  fontSize: '$4',
  textAlign: 'center',
  color: 'var(--color-text-light)',
});

const Bar = (props: BarProps) => {
  const { colorIndex, visible, formatValue, value, height, handleId } = props;

  const id = useId();

  useEffect(() => {
    if (id) {
      handleId(id);
    }
  }, [id, handleId]);

  return (
    <BarRoot>
      <StyledBar
        animate={{ height: visible ? height(value) + '%' : 0 }}
        css={{
          backgroundColor: [
            'var(--color-primary)',
            'var(--color-secondary)',
            'var(--color-tertiary)',
          ][colorIndex % 3],
        }}
        initial={{ height: 0 }}
        transition={{
          height: {
            type: 'spring',
            damping: 15,
            mass: 1,
            stiffness: 100,
          },
        }}
      />
      <Value aria-describedby={id}>{formatValue(value)}</Value>
    </BarRoot>
  );
};
